import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { WritingBlock, WritingImage } from "./writings";
import { splitWritingIntoSections } from "./writingUtils";

type WritingArticleProps = {
  body: WritingBlock[];
  headingIds: Map<number, string>;
  lead: React.ReactNode;
  leadFooter?: React.ReactNode;
};

const READING_LINE = 0.45;

// The active section is the one whose center sits closest to the reading line.
function getActiveSectionId(
  sections: { id: string }[],
  sectionRefs: Map<string, HTMLElement>,
  viewportHeight: number
): string | null {
  let activeId: string | null = sections[0]?.id ?? null;
  let bestDistance = Infinity;

  sections.forEach((section) => {
    const element = sectionRefs.get(section.id);
    if (!element) return;

    const rect = element.getBoundingClientRect();
    const center = rect.top + rect.height / 2;
    const distance = Math.abs(center - viewportHeight * READING_LINE);

    if (distance < bestDistance) {
      bestDistance = distance;
      activeId = section.id;
    }
  });

  return activeId;
}

const WritingArticle: React.FC<WritingArticleProps> = ({ body, headingIds, lead, leadFooter }) => {
  const sections = useMemo(() => {
    const split = splitWritingIntoSections(body, headingIds);
    return split.length > 0 ? split : [{ id: "intro", blocks: [] }];
  }, [body, headingIds]);

  // `null` means "not measured yet" — every section renders visible so there's
  // no flash of hidden content before the first scroll calculation.
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  // Scroll direction flips the stagger order: top-first going down, bottom-first
  // going back up so previous content reveals from the bottom.
  const [scrollDirection, setScrollDirection] = useState<"down" | "up">("down");
  // The image currently expanded in the lightbox, or null when closed.
  const [lightboxImage, setLightboxImage] = useState<WritingImage | null>(null);
  const lastScrollY = useRef(0);
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());

  // While the lightbox is open, close on Escape and lock background scroll.
  useEffect(() => {
    if (!lightboxImage) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setLightboxImage(null);
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [lightboxImage]);

  const setSectionRef = useCallback((id: string, element: HTMLElement | null) => {
    if (element) {
      sectionRefs.current.set(id, element);
    } else {
      sectionRefs.current.delete(id);
    }
  }, []);

  const updateActiveSection = useCallback(() => {
    const viewportHeight = window.innerHeight;
    const hasLayout = Array.from(sectionRefs.current.values()).some(
      (element) => element.getBoundingClientRect().height > 0
    );

    if (!hasLayout) {
      setActiveSectionId(null);
      return;
    }

    const activeId = getActiveSectionId(sections, sectionRefs.current, viewportHeight);
    setActiveSectionId((current) => (current === activeId ? current : activeId));
  }, [sections]);

  useEffect(() => {
    updateActiveSection();

    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const y = window.scrollY;
        if (y !== lastScrollY.current) {
          const dir = y > lastScrollY.current ? "down" : "up";
          lastScrollY.current = y;
          setScrollDirection((current) => (current === dir ? current : dir));
        }
        updateActiveSection();
      });
    };

    // Layout can settle after mount (fonts, view transitions, scroll changes),
    // so recompute a few times to avoid a stale active section leaving the page
    // blank when nothing else triggers a recalculation.
    const settleTimers = [0, 120, 360].map((delay) =>
      window.setTimeout(updateActiveSection, delay)
    );
    window.addEventListener("load", updateActiveSection);
    const fonts = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts;
    fonts?.ready?.then(() => updateActiveSection());

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateActiveSection);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateActiveSection);
      window.removeEventListener("load", updateActiveSection);
      settleTimers.forEach((timer) => window.clearTimeout(timer));
      if (frame) cancelAnimationFrame(frame);
    };
  }, [updateActiveSection]);

  const renderBlock = (block: WritingBlock, index: number, isIntroSection: boolean) => {
    if (block.type === "paragraph") {
      return (
        <p className={`m-0 text-sm text-secondary ${block.bold ? "font-medium" : ""}`}>{block.text}</p>
      );
    }

    if (block.type === "heading") {
      const afterQuote = body[index - 1]?.type === "blockquote";

      return (
        <h2
          id={headingIds.get(index)}
          className={`m-0 scroll-mt-20 text-base font-medium text-primary ${afterQuote ? "" : "mt-8"}`}
        >
          {block.text}
        </h2>
      );
    }

    if (block.type === "blockquote") {
      const followedByHeading = body[index + 1]?.type === "heading";
      const quoteClassName = isIntroSection
        ? "writing-cover-quote m-0 max-w-[320px] text-left text-base font-normal leading-[1.5] text-secondary"
        : `m-0 border-l-2 border-quote pl-4 text-sm text-secondary ${followedByHeading ? "mb-2" : ""}`;

      return (
        <blockquote className={quoteClassName}>{block.text}</blockquote>
      );
    }

    if (block.type === "image") {
      return (
        <figure className="m-0">
          <button
            type="button"
            className="writing-image-button focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-focus-ring"
            onClick={() => setLightboxImage(block.image)}
            aria-label={`Expand image: ${block.image.alt}`}
          >
            <img
              className="block w-full rounded-[4px]"
              src={block.image.src}
              alt={block.image.alt}
              loading="lazy"
            />
          </button>
        </figure>
      );
    }

    return (
      <div className="flex flex-col gap-3">
        {block.images.map((image) => (
          <figure key={image.src} className="m-0">
            <button
              type="button"
              className="writing-image-button focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-focus-ring"
              onClick={() => setLightboxImage(image)}
              aria-label={`Expand image: ${image.alt}`}
            >
              <img className="block w-full rounded-[4px]" src={image.src} alt={image.alt} loading="lazy" />
            </button>
          </figure>
        ))}
      </div>
    );
  };

  return (
    <>
    <article
      className={`writing-sections flex w-full min-w-0 flex-col ${
        scrollDirection === "up" ? "writing-sections--up" : ""
      }`}
    >
      {sections.map((section) => {
        const isIntro = section.id === "intro";
        const isActive = activeSectionId === null || activeSectionId === section.id;
        // Per-section child position drives the stagger; reset for each section.
        let childIndex = 0;
        const hasLeadFooter = isIntro && leadFooter != null;
        const childCount = section.blocks.length + (isIntro ? 1 : 0) + (hasLeadFooter ? 1 : 0);

        return (
          <section
            key={section.id}
            data-section-id={section.id}
            ref={(element) => setSectionRef(section.id, element)}
            className={`writing-section flex flex-col gap-6 ${isIntro ? "writing-section--intro" : ""} ${
              isActive ? "is-active" : ""
            }`}
          >
            {isIntro ? (
              <div
                className="writing-block"
                style={{ "--stagger-index": childIndex++, "--stagger-count": childCount } as React.CSSProperties}
              >
                {lead}
              </div>
            ) : null}
            {section.blocks.map(({ block, index }) => (
              <div
                key={`${block.type}-${index}`}
                className="writing-block"
                style={{ "--stagger-index": childIndex++, "--stagger-count": childCount } as React.CSSProperties}
              >
                {renderBlock(block, index, isIntro)}
              </div>
            ))}
            {hasLeadFooter ? (
              <div
                className="writing-block"
                style={{ "--stagger-index": childIndex++, "--stagger-count": childCount } as React.CSSProperties}
              >
                {leadFooter}
              </div>
            ) : null}
          </section>
        );
      })}
    </article>
    {lightboxImage
      ? createPortal(
          <div
            className="writing-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={lightboxImage.alt}
            onClick={() => setLightboxImage(null)}
          >
            <img
              className="writing-lightbox__img"
              src={lightboxImage.src}
              alt={lightboxImage.alt}
            />
          </div>,
          document.body
        )
      : null}
    </>
  );
};

export default WritingArticle;
